import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Users, Clipboard, Grid, Building2, Timer, FileText, FileSignature, Layers, ClipboardList, Menu, X, Inbox, Clock, ScrollText, ShieldCheck, Receipt, MessageCircle, IdCard, ChevronDown, Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AvailableWorkers from '../components/Dashboard/AvailableWorkers';
import AssignmentTracker from '../components/Dashboard/AssignmentTracker';
import WorkerCalendar from '../components/Dashboard/WorkerCalendar';
import ClientManagement from '../components/Dashboard/ClientManagement';
import ShiftTemplates from '../components/Dashboard/ShiftTemplates';
import Documents from '../components/Dashboard/Documents';
import ContactSubmissions from '../components/Dashboard/ContactSubmissions';
import Urenlijst from '../components/Dashboard/Urenlijst';
import AuditLog from '../components/Dashboard/AuditLog';
import Certificates from '../components/Dashboard/Certificates';
import PurchaseOrders from '../components/Dashboard/PurchaseOrders';
import WhatsAppKoppeling from '../components/Dashboard/WhatsAppKoppeling';
import Contracts from '../components/Dashboard/ZZP/Contracts';
import Functiegroepen from '../components/Dashboard/ZZP/Functiegroepen';
import Werkzaamheden from '../components/Dashboard/ZZP/Werkzaamheden';
import Opdrachtgevers from '../components/Dashboard/ZZP/Opdrachtgevers';
import IdVaststelling from '../components/Dashboard/ZZP/IdVaststelling';
import Meldingen from '../components/Dashboard/Meldingen';
import './Dashboard.css';
import './DashboardNieuw.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  return fetch(url, { ...options, headers, credentials: 'include' });
}

// Menu in groepen; een groep klapt open/dicht. De groep met de actieve pagina staat altijd open.
const NAV_GROUPS = [
  { id: 'planning', label: 'Planning', items: [
    { tab: 'calendar', label: 'Planning', Icon: Grid },
    { tab: 'meldingen', label: 'Meldingen', Icon: Bell },
    { tab: 'assignments', label: 'Toewijzingen', Icon: Clipboard },
    { tab: 'shifts', label: 'Diensten', Icon: Timer },
    { tab: 'urenlijst', label: 'Urenlijsten', Icon: Clock },
  ] },
  { id: 'personeel', label: 'Personeel', items: [
    { tab: 'workers', label: 'Medewerkers', Icon: Users },
    { tab: 'certificaten', label: 'Certificaten', Icon: ShieldCheck },
    { tab: 'documents', label: 'Documenten', Icon: FileText },
    { tab: 'id-vaststelling', label: 'ID Vaststelling', Icon: IdCard },
    { tab: 'aanvragen', label: 'Inschrijvingen', Icon: Inbox },
  ] },
  { id: 'klanten', label: 'Klanten & contracten', items: [
    { tab: 'clients', label: 'Klanten', Icon: Building2 },
    { tab: 'zzp-opdrachtgevers', label: 'Opdrachtgevers', Icon: Building2 },
    { tab: 'zzp-contracts', label: 'ZZP Contracten', Icon: FileSignature },
    { tab: 'po', label: 'PO', Icon: Receipt },
  ] },
  { id: 'instellingen', label: 'Instellingen', items: [
    { tab: 'zzp-functiegroepen', label: 'Functiegroepen', Icon: Layers },
    { tab: 'zzp-werkzaamheden', label: 'Werkzaamheden', Icon: ClipboardList },
    { tab: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle },
    { tab: 'logboek', label: 'Logboek', Icon: ScrollText },
  ] },
];

export default function Dashboard({ tab = 'calendar', skin = '' }) {
  const base = skin === 'nieuw' ? '/nieuw' : '/dashboard';
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(tab);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Lock body scroll when mobile drawer is open, and close on Esc
  useEffect(() => {
    if (!mobileNavOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setMobileNavOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [mobileNavOpen]);

  const [workers, setWorkers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [clients, setClients] = useState([]);
  const [skills, setSkills] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [workerOrder, setWorkerOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ozel_worker_order') || '[]'); } catch { return []; }
  });

  // Certificate counts drive the badge on the Certificaten tab.
  const [certCounts, setCertCounts] = useState({ red: 0, yellow: 0 });
  // Ongelezen berichten van medewerkers (portaal /mijn).
  const [meldingCount, setMeldingCount] = useState(0);
  const loadMeldingCount = useCallback(() => {
    authFetch('/api/mw-beheer/meldingen/aantal').then((r) => (r.ok ? r.json() : { ongelezen: 0 }))
      .then((d) => setMeldingCount(Number(d.ongelezen) || 0)).catch(() => {});
  }, []);
  // Inschrijvingen die nog geaccordeerd moeten worden.
  const [aanmeldCount, setAanmeldCount] = useState(0);
  const loadAanmeldCount = useCallback(() => {
    authFetch('/api/registratie/aanmeldingen/aantal').then((r) => (r.ok ? r.json() : { aantal: 0 }))
      .then((d) => setAanmeldCount(Number(d.aantal) || 0)).catch(() => {});
  }, []);
  useEffect(() => {
    loadMeldingCount();
    loadAanmeldCount();
    const t = setInterval(() => { loadMeldingCount(); loadAanmeldCount(); }, 60000);
    return () => clearInterval(t);
  }, [loadMeldingCount, loadAanmeldCount]);

  const reloadWorkers = useCallback(() => {
    authFetch('/api/workers').then((r) => r.json()).then(setWorkers).catch(console.error);
  }, []);

  useEffect(() => {
    authFetch('/api/workers').then((r) => r.json()).then(setWorkers).catch(console.error);
    authFetch('/api/clients').then((r) => r.json()).then(setClients).catch(console.error);
    authFetch('/api/assignments').then((r) => r.json()).then(setAssignments).catch(console.error);
    authFetch('/api/skills').then((r) => r.json()).then(setSkills).catch(console.error);
    authFetch('/api/shifts').then((r) => r.json()).then(setShifts).catch(console.error);
    // Badge count, so expiring certificates are visible without opening the tab.
    authFetch('/api/certificates')
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        const arr = Array.isArray(list) ? list : [];
        setCertCounts({
          red: arr.filter((c) => c.status === 'red').length,
          yellow: arr.filter((c) => c.status === 'yellow').length,
        });
      })
      .catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ozel_nav_groups')) || { planning: true }; } catch { return { planning: true }; }
  });
  const toggleGroup = (id) => setOpenGroups((g) => {
    const isActiveGroup = NAV_GROUPS.find((x) => x.id === id)?.items.some((i) => i.tab === activeTab);
    const cur = g[id] ?? isActiveGroup;
    const n = { ...g, [id]: !cur };
    try { localStorage.setItem('ozel_nav_groups', JSON.stringify(n)); } catch { /* ok */ }
    return n;
  });
  const switchTab = (tabName) => {
    const grp = NAV_GROUPS.find((x) => x.items.some((i) => i.tab === tabName));
    if (grp && openGroups[grp.id] === false) setOpenGroups((g) => ({ ...g, [grp.id]: true }));
    setActiveTab(tabName);
    setMobileNavOpen(false);
    navigate(`${base}/${tabName}`);
  };

  const updateWorkerOrder = (ids) => {
    setWorkerOrder(ids);
    try { localStorage.setItem('ozel_worker_order', JSON.stringify(ids)); } catch {}
  };

  const addAssignment = async (assignment) => {
    try {
      const res = await authFetch('/api/assignments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assignment),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        console.error('addAssignment error', d);
        // Return the reason instead of alerting here: callers that schedule in a
        // loop (multi-day, copy-week) must show ONE message, not one per day.
        return { ok: false, blocked: res.status === 409, error: d.error || 'Inplannen mislukt' };
      }
      const created = await res.json();
      setAssignments((prev) => [...prev, created]);
      return { ok: true };
    } catch (err) {
      console.error('addAssignment', err);
      return { ok: false, blocked: false, error: 'Inplannen mislukt' };
    }
  };

  const deleteAssignment = async (id) => {
    try {
      await authFetch(`/api/assignments/${id}`, { method: 'DELETE' });
      setAssignments((prev) => prev.filter((a) => a.id !== id));
    } catch (err) { console.error('deleteAssignment', err); }
  };

  const updateAssignment = async ({ id, date, time, client }) => {
    try {
      const res = await authFetch(`/api/assignments/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, time, client }),
      });
      if (!res.ok) { console.error('updateAssignment error', await res.json()); return; }
      const updated = await res.json();
      setAssignments((prev) => prev.map((a) => a.id === id ? updated : a));
    } catch (err) { console.error('updateAssignment', err); }
  };

  const addWorker = async (workerData) => {
    try {
      const res = await authFetch('/api/workers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(workerData),
      });
      if (!res.ok) { console.error('addWorker error', await res.json()); return; }
      const created = await res.json();
      setWorkers((prev) => [...prev, created]);
    } catch (err) { console.error('addWorker', err); }
  };

  const editWorker = async (id, workerData) => {
    try {
      const res = await authFetch(`/api/workers/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(workerData),
      });
      if (!res.ok) return;
      const updated = await res.json();
      setWorkers((prev) => prev.map((w) => w.id === id ? updated : w));
    } catch (err) { console.error('editWorker', err); }
  };

  const deleteWorker = async (id) => {
    try {
      await authFetch(`/api/workers/${id}`, { method: 'DELETE' });
      setWorkers((prev) => prev.filter((w) => w.id !== id));
    } catch (err) { console.error('deleteWorker', err); }
  };

  // Assign one opdrachtgever (bedrijf) to many workers at once.
  const bulkAssignOpdrachtgever = async (workerIds, opdrachtgeverId) => {
    const res = await authFetch('/api/workers/bulk-opdrachtgever', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ workerIds, opdrachtgeverId: opdrachtgeverId || null }),
    });
    if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Mislukt'); }
    const { opdrachtgeverId: ogId, opdrachtgeverName } = await res.json();
    const idSet = new Set(workerIds.map(Number));
    setWorkers((prev) => prev.map((w) =>
      idSet.has(w.id) ? { ...w, opdrachtgeverId: ogId || 0, opdrachtgeverName: opdrachtgeverName || '' } : w
    ));
  };

  const addClient = async (clientData) => {
    try {
      const res = await authFetch('/api/clients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clientData),
      });
      if (!res.ok) { console.error('addClient error', await res.json()); return; }
      const created = await res.json();
      setClients((prev) => [...prev, created]);
    } catch (err) { console.error('addClient', err); }
  };

  const deleteClient = async (clientId) => {
    try {
      await authFetch(`/api/clients/${clientId}`, { method: 'DELETE' });
      setClients((prev) => prev.filter((c) => c.id !== clientId));
    } catch (err) { console.error('deleteClient', err); }
  };

  const addSkill = async (name) => {
    const res = await authFetch('/api/skills', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
    const created = await res.json();
    setSkills((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
    return created;
  };

  const deleteSkill = async (id) => {
    try {
      await authFetch(`/api/skills/${id}`, { method: 'DELETE' });
      setSkills((prev) => prev.filter((s) => s.id !== id));
    } catch (err) { console.error('deleteSkill', err); }
  };

  const addShift = async (shiftData) => {
    const res = await authFetch('/api/shifts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(shiftData),
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
    const created = await res.json();
    setShifts((prev) => [...prev, created].sort((a, b) => a.startTime.localeCompare(b.startTime)));
    return created;
  };

  const deleteShift = async (id) => {
    try {
      await authFetch(`/api/shifts/${id}`, { method: 'DELETE' });
      setShifts((prev) => prev.filter((s) => s.id !== id));
    } catch (err) { console.error('deleteShift', err); }
  };

  const updateAssignmentStatus = async (id, status) => {
    try {
      await authFetch(`/api/assignments/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      setAssignments((prev) => prev.map((a) => a.id === id ? { ...a, status } : a));
    } catch (err) { console.error('updateAssignmentStatus', err); }
  };

  return (
    <div className={`dashboard ${skin === 'nieuw' ? 'nv' : ''}`}>
      <header className="dashboard-header">
        <div className="header-content">
          <button
            className="mobile-nav-toggle"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Menu openen"
            aria-expanded={mobileNavOpen}
          >
            <Menu size={22} />
          </button>
          <div className="logo-section">
            <h1>Özel Dashboard</h1>
            <p className="subtitle">Personeelsplanning</p>
          </div>
          <div className="header-actions">
            {skin === 'nieuw' && (
              <a className="nv-switch" href={`/dashboard/${activeTab}`} title="Terug naar de huidige versie">Oude weergave</a>
            )}
            {skin === 'nieuw' && <span className="nv-avatar" aria-hidden="true">{(user?.email || user?.fullName || '?').charAt(0).toUpperCase()}</span>}
            <span className="user-email">{user?.email || user?.fullName || ''}</span>
            <button onClick={handleLogout} className="logout-btn">
              <LogOut size={18} />
              Uitloggen
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-container">
        {mobileNavOpen && (
          <div
            className="mobile-nav-backdrop"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden="true"
          />
        )}
        <nav className={`dashboard-nav ${mobileNavOpen ? 'mobile-open' : ''}`}>
          <div className="mobile-nav-header">
            <span>Menu</span>
            <button
              className="mobile-nav-close"
              onClick={() => setMobileNavOpen(false)}
              aria-label="Menu sluiten"
            >
              <X size={20} />
            </button>
          </div>
          {NAV_GROUPS.map((g) => {
            const open = openGroups[g.id] ?? g.items.some((i) => i.tab === activeTab);
            const certBadge = g.items.some((i) => i.tab === 'certificaten') ? certCounts.red + certCounts.yellow : 0;
            const mBadge = g.items.some((i) => i.tab === 'meldingen') ? meldingCount : 0;
            const aBadge = g.items.some((i) => i.tab === 'workers') ? aanmeldCount : 0;
            const badge = certBadge + mBadge + aBadge;
            const badgeCls = mBadge > 0 || aBadge > 0 || certCounts.red > 0 ? 'nav-badge-red' : 'nav-badge-yellow';
            return (
              <div key={g.id} className={`nav-group ${open ? 'is-open' : ''}`}>
                <button type="button" className="nav-group-head" onClick={() => toggleGroup(g.id)} aria-expanded={open}>
                  <span>{g.label}</span>
                  {!open && badge > 0 && (
                    <span className={`nav-badge ${badgeCls}`}>{badge}</span>
                  )}
                  <ChevronDown size={15} className="nav-group-chevron" />
                </button>
                {open && g.items.map(({ tab, label, Icon }) => (
                  <button key={tab} className={`nav-item ${activeTab === tab ? 'active' : ''}`} onClick={() => switchTab(tab)}>
                    <Icon size={18} />
                    <span>{label}</span>
                    {tab === 'certificaten' && certBadge > 0 && (
                      <span className={`nav-badge ${certCounts.red > 0 ? 'nav-badge-red' : 'nav-badge-yellow'}`}>{certBadge}</span>
                    )}
                    {tab === 'meldingen' && mBadge > 0 && <span className="nav-badge nav-badge-red">{mBadge}</span>}
                    {tab === 'workers' && aBadge > 0 && <span className="nav-badge nav-badge-red">{aBadge}</span>}
                  </button>
                ))}
              </div>
            );
          })}
        </nav>

        <main className="dashboard-content">
          {meldingCount > 0 && activeTab !== 'meldingen' && (
            <button type="button" className="mld-banner" onClick={() => switchTab('meldingen')}>
              <Bell size={16} />
              <span>{meldingCount === 1 ? '1 nieuw bericht' : `${meldingCount} nieuwe berichten`} van medewerkers</span>
              <strong>Bekijken</strong>
            </button>
          )}
          {aanmeldCount > 0 && activeTab !== 'workers' && (
            <button type="button" className="mld-banner" onClick={() => switchTab('workers')}>
              <Users size={16} />
              <span>{aanmeldCount === 1 ? '1 nieuwe aanmelding wacht' : `${aanmeldCount} nieuwe aanmeldingen wachten`} op akkoord</span>
              <strong>Bekijken</strong>
            </button>
          )}
          {skin === 'nieuw' && (
            <div className="nv-pagehead">
              <h2>{NAV_GROUPS.flatMap((g) => g.items).find((i) => i.tab === activeTab)?.label || 'Dashboard'}</h2>
            </div>
          )}
          {activeTab === 'shifts' && (
            <ShiftTemplates
              shifts={shifts}
              onAddShift={addShift}
              onDeleteShift={deleteShift}
            />
          )}
          {activeTab === 'calendar' && (
            <WorkerCalendar
              assignments={assignments}
              workers={workers}
              clients={clients}
              shifts={shifts}
              onSchedule={addAssignment}
              onDeleteSchedule={deleteAssignment}
              onUpdateSchedule={updateAssignment}
              onNavigate={(t) => switchTab(t)}
              skin={skin}
              workerOrder={workerOrder}
              onSetWorkerOrder={updateWorkerOrder}
            />
          )}
          {activeTab === 'workers' && (
            <AvailableWorkers
              workers={workers}
              onWorkersChanged={reloadWorkers}
              onAanmeldingenCount={setAanmeldCount}
              onAddWorker={addWorker}
              onEditWorker={editWorker}
              onDeleteWorker={deleteWorker}
              onBulkAssignOpdrachtgever={bulkAssignOpdrachtgever}
              onCertificatesChanged={reloadWorkers}
              assignments={assignments}
              skills={skills}
              onAddSkill={addSkill}
              onDeleteSkill={deleteSkill}
            />
          )}
          {activeTab === 'assignments' && (
            <AssignmentTracker
              assignments={assignments}
              onStatusChange={updateAssignmentStatus}
            />
          )}
          {activeTab === 'clients' && (
            <ClientManagement
              clients={clients}
              onAddClient={addClient}
              onDeleteClient={deleteClient}
            />
          )}
          {activeTab === 'certificaten' && <Certificates onCountsChange={setCertCounts} />}
          {activeTab === 'po' && <PurchaseOrders />}
          {activeTab === 'whatsapp' && <WhatsAppKoppeling />}
          {activeTab === 'aanvragen' && <ContactSubmissions />}
          {activeTab === 'logboek' && <AuditLog />}
          {activeTab === 'urenlijst' && <Urenlijst />}
          {activeTab === 'documents' && <Documents />}
          {activeTab === 'zzp-contracts' && <Contracts />}
          {activeTab === 'zzp-functiegroepen' && <Functiegroepen />}
          {activeTab === 'zzp-werkzaamheden' && <Werkzaamheden />}
          {activeTab === 'zzp-opdrachtgevers' && <Opdrachtgevers />}
          {activeTab === 'id-vaststelling' && <IdVaststelling />}
          {activeTab === 'meldingen' && <Meldingen onChange={loadMeldingCount} />}
        </main>
      </div>
    </div>
  );
}
